import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  ListItemText,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Autocomplete, Chip, Stack, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { CloseXIcon } from "../ChatiaIcons";
import CustomToggleSwitch from "../CustomToggleSwitch";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  dialogPaper: {
    ...(theme.chatiaDS?.dialogPaper || {}),
  },
  header: {
    ...(theme.chatiaDS?.header || {}),
    flexShrink: 0,
  },
  headerTitle: {
    ...(theme.chatiaDS?.headerTitle || {}),
  },
  closeButton: {
    backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
    color: theme.chatiaDS?.closeButton?.color || "#ffffff",
    transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
      color: "#ffffff",
    },
  },
  content: {
    padding: "18px 20px !important",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 14,
  },
  fieldBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.chatiaDS?.colors?.textSecondary || (theme.palette.type === "dark" ? "#8696a0" : "#667781"),
  },
  helperText: {
    fontSize: 12,
    color: theme.chatiaDS?.colors?.textMuted || "#8696a0",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${theme.chatiaDS?.colors?.border || (theme.palette.type === "dark" ? "#202632" : "#e8e8e8")}`,
  },
  selectInput: {
    borderRadius: "12px !important",
    "& .MuiSelect-outlined": {
      borderRadius: "12px !important",
      paddingTop: 12,
      paddingBottom: 12,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
    },
  },
  menuPaper: {
    borderRadius: "14px !important",
    boxShadow: "0 10px 36px rgba(0,0,0,0.14) !important",
    border: "none",
    background: `${theme.palette.type === "dark" ? "#121212" : "#ffffff"} !important`,
    backgroundImage: "none !important",
    marginTop: 8,
    overflow: "hidden",
    "& .MuiList-root": {
      padding: 6,
    },
    "& .MuiMenuItem-root": {
      borderRadius: 8,
      padding: "9px 12px",
      marginBottom: 4,
      fontSize: 14,
      fontWeight: 500,
      color: theme.palette.type === "dark" ? "#ffffff" : "#1a1a1a",
      "&:hover": {
        background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
      "&.Mui-selected": {
        background: "rgba(6, 207, 156, 0.10)",
      },
      "&.Mui-selected:hover": {
        background: "rgba(6, 207, 156, 0.15)",
      },
    },
  },
  textField: {
    width: "100%",
  },
}));

const CampaignModalPhrase = ({ open, onClose, FlowCampaignId, onSave, defaultWhatsappId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { companyId } = user;
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const autocompletePaperSx = {
    borderRadius: "14px",
    boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
    border: "none",
    background: isDark ? "#121212" : "#ffffff",
    backgroundImage: "none",
    mt: 1,
    overflow: "hidden",
    "& .MuiAutocomplete-listbox": {
      padding: "6px",
      "& .MuiAutocomplete-option": {
        borderRadius: "8px",
        padding: "9px 12px",
        marginBottom: "4px",
        fontSize: 14,
        fontWeight: 500,
        color: isDark ? "#ffffff" : "#1a1a1a",
        "&:hover, &.Mui-focused, &[data-focus='true']": {
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        },
        "&[aria-selected='true']": {
          background: "rgba(6, 207, 156, 0.10)",
        },
      },
    },
  };

  const [campaignEditable, setCampaignEditable] = useState(true);
  const attachmentFile = useRef(null);

  const [dataItem, setDataItem] = useState({
    name: "",
    phrase: ""
  });

  const [dataItemError, setDataItemError] = useState({
    name: false,
    flowId: false,
    phrase: false
  });

  const [matchType, setMatchType] = useState("contains");

  const [flowSelected, setFlowSelected] = useState(null);
  const [flowsData, setFlowsData] = useState([]);
  const [flowsDataComplete, setFlowsDataComplete] = useState([]);

  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");

  // ✅ Aplica valor inicial da conexão
  useEffect(() => {
  if (!FlowCampaignId) {
    const stored = localStorage.getItem("selectedWhatsappId");
    if (stored) {
      console.log("Forçando selectedWhatsapp via localStorage:", stored);
      setSelectedWhatsapp(parseInt(stored));
    }
  }
}, [FlowCampaignId]);


  const [whatsAppNames, setWhatsAppNames] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);
  const [whatsAppSelected, setWhatsAppSelected] = useState({});

  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);

  const getFlows = async () => {
    const flows = await api.get("/flowbuilder");
    setFlowsDataComplete(flows.data.flows);
    setFlowsData(flows.data.flows.map(flow => flow.name));
    return flows.data.flows;
  };

  const detailsPhrase = async flows => {
    setLoading(true);
    await api.get(`/flowcampaign/${FlowCampaignId}`).then(res => {
      console.log("dete", res.data);
      setDataItem({
        name: res.data.details.name,
        phrase: res.data.details.phrase
      });
      setMatchType(res.data.details.matchType || "exact");
      setActive(res.data.details.status)
      const nameFlow = flows.filter(
        itemFlows => itemFlows.id === res.data.details.flowId
      );
      if (nameFlow.length > 0) {
        setFlowSelected(nameFlow[0].name);
        if (res.data.details.whatsappId) {
        console.log("Aplicando whatsappId do banco:", res.data.details.whatsappId);
        setSelectedWhatsapp(res.data.details.whatsappId);
     }

      }
      setLoading(false);
    });
  };

  const handleClose = () => {
    onClose();
  };

  const openModal = async () => {
    const flows = await getFlows();
    if (FlowCampaignId) {
      await detailsPhrase(flows);
    } else {
      clearData();
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => {
            setWhatsApps(data)
          })
      }
      fetchContacts();
      setLoading(false)
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [])


  useEffect(() => {
    setLoading(true)
    if (open === true) {
      openModal();
    }
  }, [open]);

  const clearErrors = () => {
    setDataItemError({
      name: false,
      flowId: false,
      whatsappId: false,
      phrase: false
    });
  };

  const clearData = () => {
    setFlowSelected(null);
    setDataItem({
      name: "",
      phrase: ""
    });
  };

  const applicationSaveAndEdit = async () => {
    let error = 0;
    if (dataItem.name === "" || dataItem.name.length === 0) {
      setDataItemError(old => ({ ...old, name: true }));
      error++;
    }
    if (!flowSelected) {
      setDataItemError(old => ({ ...old, flowId: true }));
      error++;
    }
    if (dataItem.phrase === "" || dataItem.phrase.length === 0) {
      setDataItemError(old => ({ ...old, phrase: true }));
      error++;
    }
    if(!selectedWhatsapp){
      setDataItemError(old => ({ ...old, whatsappId: true }))
    }

    if (error !== 0) {
      return;
    }

    const idFlow = flowsDataComplete.filter(
      item => item.name === flowSelected
    )[0].id;

    const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null

    try {
      if (FlowCampaignId) {
        await api.put("/flowcampaign", {
          id: FlowCampaignId,
          name: dataItem.name,
          flowId: idFlow,
          whatsappId: whatsappId,
          phrase: dataItem.phrase,
          status: active,
          matchType: matchType
        });
        toast.success(i18n.t("campaignsPhrase.phraseUpdated"));
      } else {
        await api.post("/flowcampaign", {
          name: dataItem.name,
          flowId: idFlow,
          whatsappId: whatsappId,
          phrase: dataItem.phrase,
          status: active,
          matchType: matchType
        });
        toast.success(i18n.t("campaignsPhrase.phraseCreated"));
      }

      onClose();
      clearData();
      onSave('ok');
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            {campaignEditable
              ? FlowCampaignId
                ? i18n.t("campaignsPhrase.modal.editTitle")
                : i18n.t("campaignsPhrase.modal.newTitle")
              : `${i18n.t("campaigns.dialog.readonly")}`}
          </Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <div style={{ display: "none" }}>
          <input type="file" ref={attachmentFile} />
        </div>
        {!loading && (
          <>
            <DialogContent dividers className={classes.content}>
              <div className={classes.formGrid}>
              <div className={classes.fieldBlock}>
                <Typography className={classes.fieldLabel}>{i18n.t("campaignsPhrase.modal.nameLabel")}</Typography>
                <TextField
                  label={""}
                  name="text"
                  variant="outlined"
                  error={dataItemError.name || undefined}
                  defaultValue={dataItem.name}
                  margin="dense"
                  onChange={e => {
                    setDataItem(old => {
                      let newValue = old;
                      newValue.name = e.target.value;
                      return newValue;
                    });
                  }}
                  className={classes.textField}
                />
              </div>
              <div className={classes.fieldBlock}>
                <Typography className={classes.fieldLabel}>{i18n.t("campaignsPhrase.modal.flowLabel")}</Typography>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  fullWidth
                  size="small"
                  autoHighlight
                  clearOnEscape
                  value={flowSelected || null}
                  options={flowsData}
                  onChange={(event, newValue) => {
                    setFlowSelected(newValue);
                  }}
                  componentsProps={{ paper: { sx: autocompletePaperSx } }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={dataItemError.flowId || undefined}
                      variant="outlined"
                      style={{ width: "100%" }}
                      placeholder={i18n.t("campaignsPhrase.modal.flowPlaceholder")}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        style={{ borderRadius: "8px" }}
                      />
                    ))
                  }
                />
              </div>
              <div className={classes.fieldBlock}>
                <Typography className={classes.fieldLabel}>{i18n.t("campaignsPhrase.modal.connectionPlaceholder")}</Typography>
                <Select
                  required
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  className={classes.selectInput}
                  value={selectedWhatsapp}
                  onChange={(e) => {
                    setSelectedWhatsapp(e.target.value)
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      className: classes.menuPaper,
                    },
                  }}
                  renderValue={() => {
                    if (selectedWhatsapp === "") {
                      return i18n.t("campaignsPhrase.modal.connectionPlaceholder")
                    }
                    const whatsapp = whatsApps.find(w => w.id === selectedWhatsapp)
                    return whatsapp?.name || i18n.t("campaignsPhrase.modal.connectionPlaceholder")
                  }}
                >

                  {whatsApps?.length > 0 &&
                    whatsApps.map((whatsapp, key) => (
                      <MenuItem dense key={key} value={whatsapp.id}>
                        <ListItemText
                          primary={
                            <>
                              <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
                                {whatsapp.name} &nbsp; <p className={(whatsapp.status) === 'CONNECTED' ? classes.online : classes.offline} >({whatsapp.status})</p>
                              </Typography>
                            </>
                          }
                        />
                      </MenuItem>
                    ))
                  }
                </Select>
              </div>
              <div className={classes.fieldBlock}>
                <Typography className={classes.fieldLabel}>{i18n.t("campaignsPhrase.modal.phraseLabel")}</Typography>
                <TextField
                  label={""}
                  name="text"
                  variant="outlined"
                  error={dataItemError.phrase || undefined}
                  defaultValue={dataItem.phrase}
                  margin="dense"
                  onChange={e => {
                    setDataItem(old => {
                      let newValue = old;
                      newValue.phrase = e.target.value;
                      return newValue;
                    });
                  }}
                  className={classes.textField}
                />
              </div>
              <div className={classes.fieldBlock}>
                <Typography className={classes.fieldLabel}>{i18n.t("campaignsPhrase.modal.matchTypeLabel")}</Typography>
                <RadioGroup
                  value={matchType}
                  onChange={(e) => setMatchType(e.target.value)}
                >
                  <FormControlLabel
                    value="exact"
                    control={<Radio />}
                    label={i18n.t("campaignsPhrase.modal.matchTypeExact")}
                  />
                  <FormControlLabel
                    value="contains"
                    control={<Radio />}
                    label={i18n.t("campaignsPhrase.modal.matchTypeContains")}
                  />
                </RadioGroup>
                <Typography className={classes.helperText}>
                  {i18n.t("campaignsPhrase.modal.matchTypeTooltip")}
                </Typography>
              </div>
              <div className={classes.statusRow}>
                <Typography className={classes.fieldLabel}>{i18n.t("campaignsPhrase.modal.statusLabel")}</Typography>
                <CustomToggleSwitch checked={active} onChange={() => setActive(old => !old)} />
              </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => {
                  onClose();
                  clearErrors();
                }}
              >
                {i18n.t("campaignsPhrase.modal.cancelButton")}
              </Button>
              {FlowCampaignId ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => applicationSaveAndEdit()}
                >
                  {i18n.t("campaignsPhrase.modal.saveButton")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => applicationSaveAndEdit()}
                >
                  {i18n.t("campaignsPhrase.modal.createButton")}
                </Button>
              )}
            </DialogActions>
          </>
        )}
        {loading && (
          <DialogContent dividers className={classes.content}>
            <Stack justifyContent={"center"} alignItems={"center"} minHeight={"160px"}>
              <CircularProgress />
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default CampaignModalPhrase;
